import Card from '@material-ui/core/Card';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/AddCircle';
import CheckIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { FormControl } from '@sketchpixy/rubix';
import { ctx as L20NContext, Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { connect } from 'react-redux';
import { BLUE, GREEN, PERMISSIONS, PERMISSION_ENTITIES } from '../../_config/consts';
import { isUserWithoutEmail } from '../../_config/utils';
import UsersIconCustom from '../../components/CustomIcons/UsersIconCustom.jsx';
import FormFlatContainer from '../../components/forms/Elements/FormFlatContainer.jsx';
import InfoSection from '../../components/InfoSection/InfoSection.jsx';
import ListLoadingView from '../../components/ListLoadingView.jsx';
import MDButton from '../../components/MDButton/MDButton.jsx';
import AbilityProvider from '../../permissionsUtils/AbilityProvider.js';
import * as AccessProfilesActions from '../../redux/actions/accessProfiles.actions';
import * as GuestActions from '../../redux/actions/guest.actions';
import * as ModalActions from '../../redux/actions/modal.actions';
import * as UtilsActions from '../../redux/actions/utils.actions';

const AVATAR_PALETTE = ['#1B97C2', '#4DB6AC', '#FF9800', '#7E57C2', '#EF5350', '#26A69A', '#5C6BC0', '#AB47BC'];

@connect(state => ({ accessProfiles: state.accessProfiles }))
class AccessProfilesUserList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isListLoading: false,
      isAddMode: false,
      searchText: '',
      searchResults: [],
      isSearching: false,
      pendingUserIds: {},
    };
    this.searchTimeout = null;
  }

  componentWillUnmount() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  async onFetchMoreItems(page) {
    const { dispatch, accessProfiles: { selectedAccessProfile } } = this.props;
    dispatch(UtilsActions.setViewLoading(true));
    this.setState({ isListLoading: true });
    try {
      await dispatch(AccessProfilesActions.fetchGuestsByAccessProfiles(selectedAccessProfile.id, page));
    } finally {
      dispatch(UtilsActions.setViewLoading(false));
      this.setState({ isListLoading: false });
    }
  }

  getInitials(firstname, lastname) {
    const first = firstname ? firstname.trim().charAt(0).toUpperCase() : '';
    const last = lastname ? lastname.trim().charAt(0).toUpperCase() : '';
    return `${first}${last}` || '?';
  }

  getAvatarColor(id) {
    const index = typeof id === 'number' ? Math.abs(id) % AVATAR_PALETTE.length : 0;
    return AVATAR_PALETTE[index];
  }

  onToggleAddMode() {
    this.setState(prev => ({
      isAddMode: !prev.isAddMode,
      searchText: '',
      searchResults: [],
    }));
  }

  onSearchChange(value) {
    this.setState({ searchText: value });
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (!value || value.trim().length < 2) {
      this.setState({ searchResults: [], isSearching: false });
      return;
    }
    this.setState({ isSearching: true });
    this.searchTimeout = setTimeout(() => this.runSearch(value.trim()), 400);
  }

  async runSearch(text) {
    const { dispatch } = this.props;
    try {
      const guests = await dispatch(GuestActions.fetchGuestsForSelector(text));
      this.setState({ searchResults: guests || [], isSearching: false });
    } catch (error) {
      this.setState({ searchResults: [], isSearching: false });
    }
  }

  async onAddUser(user) {
    const { dispatch, accessProfiles: { selectedAccessProfile } } = this.props;
    const { pendingUserIds, searchText } = this.state;
    if (pendingUserIds[user.id]) return;
    this.setState({ pendingUserIds: { ...pendingUserIds, [user.id]: true } });
    try {
      const outcomes = await dispatch(AccessProfilesActions.assignUsersToAccessProfile([user.id], selectedAccessProfile.id));
      const successes = _.filter(outcomes, { success: true }).length;
      const failures = _.filter(outcomes, { success: false }).length;
      if (successes > 0) {
        await dispatch(AccessProfilesActions.fetchGuestsByAccessProfiles(selectedAccessProfile.id, 0));
        if (searchText && searchText.trim().length >= 2) await this.runSearch(searchText.trim());
      }
      if (failures > 0 && successes === 0) {
        dispatch(ModalActions.showModal({
          modalType: 'ERROR_ALERT',
          modalProps: {
            message: (<h6 className="snack-title"><Entity entity="addUsersToProfileError" /></h6>),
          },
        }));
      }
    } finally {
      this.setState(prev => ({ pendingUserIds: _.omit(prev.pendingUserIds, user.id) }));
    }
  }

  renderUserCard(user, options = {}) {
    const { assignedIdMap = {}, showAddCta = false } = options;
    const { pendingUserIds } = this.state;
    const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
    const email = isUserWithoutEmail(user) ? null : user.email;
    const avatarColor = this.getAvatarColor(user.id);
    const alreadyAssigned = !!assignedIdMap[user.id];
    const isPending = !!pendingUserIds[user.id];
    return (
      <Card
        key={user.id}
        className="card card-rounded card-noHover"
        style={{ borderRadius: 10, marginBottom: 10, padding: '10px 14px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        elevation={2}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            backgroundColor: avatarColor,
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
            flexShrink: 0,
            fontSize: 13,
          }}
        >
          {this.getInitials(user.firstname, user.lastname)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <h5 style={{ color: '#3f3f3f', margin: 0, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fullName || <Entity entity="user" />}
          </h5>
          {email ? (
            <h6 style={{ color: '#7f7f7f', margin: '2px 0 0 0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {email}
            </h6>
          ) : null}
        </div>
        {showAddCta ? (
          alreadyAssigned ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CheckIcon style={{ color: GREEN, fontSize: 20, marginRight: 6 }} />
              <h6 style={{ color: GREEN, margin: 0, fontSize: 12, fontWeight: 'bold' }}>
                <Entity entity="usersAlreadyInProfile" />
              </h6>
            </div>
          ) : (
            <MDButton
              title={<Entity entity="addUserToProfileCta" />}
              backgroundColor={BLUE}
              fullWidth={false}
              isLoading={isPending}
              disabled={isPending}
              containerStyle={{ margin: 0 }}
              onClick={() => this.onAddUser(user)}
            />
          )
        ) : null}
      </Card>
    );
  }

  renderAddUsersSection(assignedIdMap) {
    const { searchText, searchResults, isSearching } = this.state;
    const filteredResults = _.filter(searchResults, user => !!user && !_.isNil(user.id));
    const placeholder = L20NContext.getSync('searchUsersByNameEmail');
    return (
      <FormFlatContainer
        title="addUsersToProfile"
        subtitle="addUsersToProfileSubtitle"
        icon={<AddIcon style={{ fontSize: 22, color: BLUE }} />}
        iconButtonAction={() => this.onToggleAddMode()}
        iconButtonIcon={<CloseIcon style={{ fontSize: 20, color: '#3f3f3f' }} />}
        containerStyle={{ marginTop: 0, marginRight: 20, marginBottom: 12 }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
          <SearchIcon style={{ color: '#7f7f7f', marginRight: 8 }} />
          <FormControl
            type="text"
            autoFocus
            value={searchText}
            placeholder={placeholder}
            className="form-control-custom"
            onChange={e => this.onSearchChange(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
        {isSearching ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <CircularProgress size={22} style={{ color: BLUE }} />
          </div>
        ) : (
          <div style={{ maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
            {searchText && searchText.trim().length >= 2 && filteredResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <h5 style={{ color: '#7f7f7f', margin: 0 }}>
                  <Entity entity="noMatchingUsersFound" />
                </h5>
              </div>
            ) : null}
            {_.map(filteredResults, user => this.renderUserCard(user, { assignedIdMap, showAddCta: true }))}
          </div>
        )}
      </FormFlatContainer>
    );
  }

  render() {
    const { accessProfiles: { guests: { content: guestsData, pagination }, selectedAccessProfile } } = this.props;
    const { isListLoading, isAddMode } = this.state;
    const totalUsers = pagination && pagination.totalElements ? pagination.totalElements : 0;
    const hasUsers = !_.isEmpty(guestsData);
    const canAssignUsers = !!(AbilityProvider.getAbilityHelper().hasPermission([PERMISSIONS.UPDATE], PERMISSION_ENTITIES.USER)
      && selectedAccessProfile && selectedAccessProfile.id);
    const assignedIdMap = _.reduce(guestsData, (acc, g) => { acc[g.id] = true; return acc; }, {});

    return (
      <div>
        <div style={{ position: 'relative', marginRight: 20, marginBottom: 12 }}>
          <InfoSection
            gravity="info"
            containerStyle={{ marginTop: 0, marginBottom: 0 }}
            title={<Entity entity="accessProfileUsersCountTitle" data={{ count: totalUsers }} />}
            subtitle="accessProfileUsersCountSubtitle"
          />
          {canAssignUsers && !isAddMode ? (
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <IconButton
                onClick={() => this.onToggleAddMode()}
                style={{ padding: 6, backgroundColor: 'rgba(255, 255, 255, 0.75)' }}
              >
                <AddIcon style={{ color: '#1976D2', fontSize: 28 }} />
              </IconButton>
            </div>
          ) : null}
        </div>
        {isAddMode && canAssignUsers ? this.renderAddUsersSection(assignedIdMap) : null}
        {hasUsers ? (
          <div style={{ padding: 8, backgroundColor: '#f6f6f6', borderRadius: 10, marginRight: 20 }}>
            <div style={{ maxHeight: 420, padding: 4, overflowY: 'auto' }}>
              <InfiniteScroll
                pageStart={0}
                useWindow={false}
                initialLoad={false}
                loadMore={() => this.onFetchMoreItems(pagination.number + 1)}
                hasMore={pagination.number + 1 < pagination.totalPages}
                loader={<ListLoadingView key="loader" containerStyle={{ backgroundColor: 'transparent' }} />}
              >
                {_.map(guestsData, user => this.renderUserCard(user))}
              </InfiniteScroll>
            </div>
          </div>
        ) : (
          <div>
            {!isListLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, backgroundColor: '#f6f6f6', borderRadius: 10, marginRight: 20 }}>
                <UsersIconCustom style={{ color: '#bdbdbd', marginTop: 3, width: 48 }} />
                <h4 style={{ color: '#7f7f7f', textAlign: 'center', marginTop: 16 }}>
                  <Entity entity="noUsersForProfile" />
                </h4>
              </div>
            ) : <ListLoadingView />}
          </div>
        )}
      </div>
    );
  }
}

export default AccessProfilesUserList;
